import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Release, ReviewClassification } from '../models/release';
import { ReviewClassificationDisplayPipe } from '../pipes/review-classification-display.pipe';
import { ReleasesDataService } from '../services/releases-data.service';
import { LoadingService } from '../services/loading.service';
import { EditReleaseDialogComponent } from '../dialog/edit-release/edit-release-dialog.component';

interface TeamReleaseGroup {
  team: string;
  releases: Release[];
}

/**
 * Routed "Release Retro" view (/releases/retro). Loads its own data via ReleasesDataService so
 * it works on direct URL navigation, and renders inside the app shell (the site nav + sidebar
 * stay functional). Shows releases grouped by team, filtered to a cutoff date and to the
 * selected failure type(s): Change Fail is Red when changeFail === false; Pipeline status is
 * Red when releaseStatus === false (the inverted convention shared with
 * ReleaseSuccessFailureRenderer and the edit dialog).
 */
@Component({
  selector: 'app-releases-presentation',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatButtonModule,
  ],
  template: `
    <div class="retro">
      <header class="retro-header">
        <div class="header-text">
          <h1>Release Retro</h1>
          <p class="subtitle">
            {{ groups.length }} {{ groups.length === 1 ? 'team' : 'teams' }} ·
            {{ totalReleases }} {{ totalReleases === 1 ? selectionLabelSingular : selectionLabelPlural }}
            @if (cutoffDate) {
              since {{ cutoffDate | date: 'EEE. M/d/yyyy' }}
            }
            @if (!loading && !hydrated) {
              · <span class="refreshing">refreshing details…</span>
            }
          </p>
        </div>
        <div class="header-controls">
          <mat-checkbox [(ngModel)]="showChangeFails" (change)="buildGroups()">Change fails</mat-checkbox>
          <mat-checkbox [(ngModel)]="showPipelineFails" (change)="buildGroups()">Pipeline failures</mat-checkbox>
          <mat-form-field appearance="outline" class="cutoff-field" subscriptSizing="dynamic">
            <mat-label>Show releases since</mat-label>
            <input
              matInput
              [matDatepicker]="cutoffPicker"
              [value]="cutoffDate"
              (dateChange)="onCutoffChange($event.value)"
              readonly
            />
            <mat-datepicker-toggle matIconSuffix [for]="cutoffPicker"></mat-datepicker-toggle>
            <mat-datepicker #cutoffPicker></mat-datepicker>
          </mat-form-field>
          <button mat-raised-button class="back-button" routerLink="/releases">
            <mat-icon>arrow_back</mat-icon>
            Back to Releases
          </button>
        </div>
      </header>

      @if (loading) {
        <div class="empty-state">Loading releases…</div>
      } @else if (!showChangeFails && !showPipelineFails) {
        <div class="empty-state">Select a failure type to display.</div>
      } @else if (groups.length === 0) {
        <div class="empty-state">No matching releases in the selected window. 🎉</div>
      }

      <div class="teams-grid">
        @for (group of groups; track group.team) {
          <section class="team-card">
            <div class="team-card-header">
              <h2>{{ group.team || 'Unassigned' }}</h2>
              <span class="badge failed">{{ group.releases.length }}</span>
            </div>
            <table class="release-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Pipeline</th>
                  <th>Release</th>
                  <th class="center">Status</th>
                  <th class="center">Change Fail</th>
                  <th>Review</th>
                  <th class="center">Edit</th>
                </tr>
              </thead>
              <tbody>
                @for (release of group.releases; track release.id) {
                  <tr
                    class="clickable-row"
                    (click)="openEdit(release)"
                    (keydown.enter)="openEdit(release)"
                    tabindex="0"
                    title="Edit release details"
                  >
                    <td class="nowrap">{{ release.releaseStartedDateLocal | date: 'M/d/yy, h:mm a' }}</td>
                    <td>{{ release.pipelineName }}</td>
                    <td>
                      <a
                        [href]="release.releaseLink"
                        target="_blank"
                        rel="noopener noreferrer"
                        (click)="$event.stopPropagation()"
                      >{{ release.releaseName }}</a>
                    </td>
                    <td class="center">
                      <span class="status-dot" [class.success]="release.releaseStatus" [class.failure]="!release.releaseStatus"></span>
                    </td>
                    <td class="center">
                      <span class="status-dot" [class.success]="release.changeFail" [class.failure]="!release.changeFail"></span>
                    </td>
                    <td>{{ classificationLabel(release) }}</td>
                    <td class="center"><mat-icon class="edit-icon">edit</mat-icon></td>
                  </tr>
                }
              </tbody>
            </table>
          </section>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .retro {
        padding: 16px 24px 48px;
      }

      .retro-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        flex-wrap: wrap;
        padding-bottom: 12px;
        border-bottom: 3px solid #0093a1;
        margin-bottom: 24px;
      }

      .header-text h1 {
        margin: 0;
        color: #0093a1;
        font-weight: 750;
        font-size: 2rem;
      }

      .subtitle {
        margin: 4px 0 0;
        color: #555;
      }

      .subtitle .refreshing {
        color: #0093a1;
        font-style: italic;
      }

      .header-controls {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
      }

      .header-controls .cutoff-field {
        width: 220px;
      }

      .back-button mat-icon {
        margin-right: 4px;
      }

      .empty-state {
        text-align: center;
        color: #777;
        padding: 48px 0;
        font-size: 1.1rem;
      }

      .teams-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(520px, 1fr));
        gap: 24px;
      }

      .team-card {
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
        padding: 16px 20px;
        break-inside: avoid;
      }

      .team-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;
      }

      .team-card-header h2 {
        margin: 0;
        color: #0093a1;
        font-size: 1.3rem;
      }

      .badge {
        border-radius: 12px;
        padding: 2px 10px;
        font-size: 0.8rem;
        font-weight: 600;
        background: rgba(220, 53, 69, 0.12);
        color: #dc3545;
      }

      .release-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.9rem;
      }

      .release-table th,
      .release-table td {
        text-align: left;
        padding: 6px 8px;
        border-bottom: 1px solid #eee;
        color: #333;
      }

      .release-table th {
        color: #666;
        font-weight: 600;
        border-bottom: 2px solid #ddd;
      }

      .release-table .center {
        text-align: center;
      }

      .release-table .nowrap {
        white-space: nowrap;
      }

      .release-table a {
        color: #1565c0;
        text-decoration: underline;
      }

      .status-dot {
        display: inline-block;
        width: 12px;
        height: 12px;
        border-radius: 50%;
      }

      .status-dot.success {
        background: #4caf50;
      }

      .status-dot.failure {
        background: #f44336;
      }

      .clickable-row {
        cursor: pointer;
        transition: background-color 0.15s ease;
      }

      .clickable-row:hover,
      .clickable-row:focus-visible {
        background-color: rgba(0, 147, 161, 0.08);
        outline: none;
      }

      .edit-icon {
        color: #00a103;
        font-size: 18px;
        height: 18px;
        width: 18px;
        vertical-align: middle;
      }
    `,
  ],
})
export class ReleasesPresentationComponent implements OnInit, OnDestroy {
  static readonly DEFAULT_RETRO_WINDOW_DAYS = 14;
  private static readonly DAY_MS = 24 * 60 * 60 * 1000;

  releases: Release[] = [];
  groups: TeamReleaseGroup[] = [];
  totalReleases = 0;
  loading = true;
  /**
   * True once a fresh (server) emission has landed. The cached SWR paint is a trimmed projection
   * (no releaseEnvironments / description), so until this flips the edit dialog's Failed Stages and
   * descriptions can be momentarily empty. Editing the review classification still works from the
   * cached row (those fields are retained), so we surface a subtle "refreshing" hint rather than
   * blocking the click.
   */
  hydrated = false;

  cutoffDate: Date | null = ReleasesPresentationComponent.defaultCutoffDate();
  showChangeFails = true;
  showPipelineFails = false;

  /** How far back data is fetched. Starts at the shared default; widened if a cutoff predates it. */
  windowDays = ReleasesDataService.DEFAULT_WINDOW_DAYS;

  private readonly destroying$ = new Subject<void>();
  private loadSub?: Subscription;
  private readonly reviewClassificationPipe = new ReviewClassificationDisplayPipe();

  constructor(
    private readonly releasesData: ReleasesDataService,
    private readonly dialog: MatDialog,
    public readonly loadingService: LoadingService
  ) {}

  /** The default retro cutoff: midnight, DEFAULT_RETRO_WINDOW_DAYS ago. */
  static defaultCutoffDate(): Date {
    const date = new Date();
    date.setDate(date.getDate() - ReleasesPresentationComponent.DEFAULT_RETRO_WINDOW_DAYS);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  ngOnInit(): void {
    this.loadReleases(false);
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  /**
   * Loads releases for the current window. The data service emits cached data first (instant
   * paint) then fresh data, so `next` may fire more than once — each emission rebuilds the groups.
   */
  private loadReleases(forceReload: boolean): void {
    this.loading = true;
    this.hydrated = false;
    this.loadingService.show('retro');
    // Cancel any in-flight load so a superseded background refresh can't clobber newer state.
    this.loadSub?.unsubscribe();
    this.loadSub = this.releasesData
      .getReleases(this.windowDays, forceReload)
      .pipe(takeUntil(this.destroying$))
      .subscribe({
        next: (releases) => {
          this.releases = releases;
          this.buildGroups();
          this.loading = false;
          this.loadingService.hide('retro');
        },
        error: (error) => {
          console.error('Error loading releases for retro:', error);
          this.loading = false;
          this.loadingService.hide('retro');
        },
        // getReleases completes only after the fresh server emission (the cached paint is mid-stream
        // in the concat), so completion means the full release objects are now in hand.
        complete: () => {
          this.hydrated = true;
        },
      });
  }

  get selectionLabelSingular(): string {
    if (this.showChangeFails && this.showPipelineFails) return 'failure';
    if (this.showPipelineFails) return 'pipeline failure';
    return 'change fail';
  }

  get selectionLabelPlural(): string {
    if (this.showChangeFails && this.showPipelineFails) return 'failures';
    if (this.showPipelineFails) return 'pipeline failures';
    return 'change fails';
  }

  onCutoffChange(date: Date | null): void {
    this.cutoffDate = date;
    if (date) {
      const daysBack = Math.ceil(
        (Date.now() - date.getTime()) / ReleasesPresentationComponent.DAY_MS
      );
      if (daysBack > this.windowDays) {
        // The chosen cutoff predates the fetched window — widen it (plus a buffer) and reload.
        this.windowDays = daysBack + 7;
        this.loadReleases(true);
        return;
      }
    }
    this.buildGroups();
  }

  classificationLabel(release: Release): string {
    return this.reviewClassificationPipe.transform(
      release.reviewClassification ?? ReviewClassification.NeedsReview
    );
  }

  openEdit(release: Release): void {
    const dialogRef = this.dialog.open(EditReleaseDialogComponent, {
      width: '600px',
      panelClass: 'release-edit-dialog',
      data: { ...release },
    });
    dialogRef.afterClosed().subscribe((result: Release | undefined) => {
      if (result) {
        const index = this.releases.findIndex((r) => r.id === result.id);
        if (index > -1) {
          this.releases = this.releases.map((r, i) => (i === index ? result : r));
          this.buildGroups();
        }
        // The locally-applied edit is authoritative for the user's own change: cancel any in-flight
        // background refresh so a late fresh$ emission can't overwrite it, and write the edit
        // through to the cache (both views) so a reload reflects it instead of the pre-edit copy.
        this.loadSub?.unsubscribe();
        this.releasesData.applyEdit(result);
      }
    });
  }

  buildGroups(): void {
    const cutoffTime = this.cutoffDate ? this.cutoffDate.getTime() : null;
    const matched = (this.releases ?? []).filter((release) => {
      const inWindow =
        cutoffTime === null ||
        new Date(release.releaseStartedDate).getTime() >= cutoffTime;
      if (!inWindow) {
        return false;
      }
      // Red = false for both signals (inverted convention).
      const isChangeFail = this.showChangeFails && release.changeFail === false;
      const isPipelineFail = this.showPipelineFails && release.releaseStatus === false;
      return isChangeFail || isPipelineFail;
    });

    const byTeam = new Map<string, Release[]>();
    matched.forEach((release) => {
      const team = release.team ?? '';
      const list = byTeam.get(team) ?? [];
      list.push(release);
      byTeam.set(team, list);
    });

    this.groups = Array.from(byTeam.entries())
      .map(([team, releases]) => ({
        team,
        releases: [...releases].sort(
          (a, b) =>
            new Date(b.releaseStartedDate).getTime() -
            new Date(a.releaseStartedDate).getTime()
        ),
      }))
      .sort((a, b) => (a.team || 'zzz').localeCompare(b.team || 'zzz'));

    this.totalReleases = matched.length;
  }
}
